import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { showApiError } from '@/lib/utils/notifications'
import { fetchPropertyMappingReview } from '../services/propertyMappingService'
import type {
  PropertyMappingHistoryEvent,
  PropertyMappingRow,
  PropertyMatchCandidate,
} from '../types'

function formatStatus(status: string): string {
  if (status === 'MAPPED') return 'Mapped'
  if (status === 'SKIPPED') return 'Skipped'
  if (status === 'NEEDS_REVIEW') return 'Needs review'
  return status.replace(/_/g, ' ').toLowerCase()
}

function PropertyDetailCard({
  title,
  name,
  subtitle,
  fields,
  muted,
}: {
  title: string
  name: string
  subtitle?: string
  fields: Array<[string, string]>
  muted?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-[18px] border border-[#e4ebf7] bg-white p-5 shadow-sm',
        !muted && 'bg-gradient-to-b from-[#f7fbff] to-white',
      )}
    >
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">{title}</p>
      <h3 className="mb-1 text-lg font-bold text-[#16213e]">{name}</h3>
      <p className="mb-4 text-xs text-[#627089]">{subtitle || '--'}</p>
      <dl className="space-y-1 text-xs leading-6 text-[#51607a]">
        {fields.map(([label, value]) => (
          <div key={label} className="flex gap-1">
            <dt>{label}:</dt>
            <dd className="break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function PropertyMappingReviewDialogBody({
  row,
  onClose,
}: {
  row: PropertyMappingRow
  onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [candidates, setCandidates] = useState<PropertyMatchCandidate[]>([])
  const [history, setHistory] = useState<PropertyMappingHistoryEvent[]>([])

  useBodyScrollLock(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void fetchPropertyMappingReview(row.id)
      .then((result) => {
        if (cancelled) return
        setCandidates(result.candidates)
        setHistory(result.history)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [row])

  const bestCandidate = useMemo(() => {
    const mapped = candidates.find((candidate) => candidate.propertyId === row.libertyPropertyId)
    if (mapped) return mapped
    const exact = candidates.find((candidate) => candidate.nameMatch)
    if (exact) return exact
    if (row.libertyPropertyId) {
      return {
        propertyId: row.libertyPropertyId,
        propertyName: row.libertyPropertyName || `Property #${row.libertyPropertyId}`,
        address: row.libertyAddress || '',
        nameMatch: false,
        score: row.confidence ?? 0,
        reasons: [],
      } satisfies PropertyMatchCandidate
    }
    return candidates[0] ?? null
  }, [candidates, row])

  return createPortal(
    <div
      className="fixed inset-0 z-[10030] flex items-center justify-center bg-[#091430]/55 p-3 backdrop-blur-[3px] md:p-6"
      onClick={onClose}
      data-testid="property-mapping-review-overlay"
    >
      <aside
        className="relative flex h-full max-h-[calc(100vh-1.5rem)] w-full max-w-[1280px] flex-col overflow-hidden rounded-[20px] border border-[#e4ebf7] bg-gradient-to-b from-[#fbfcff] to-[#f3f7ff] shadow-[0_28px_70px_rgba(15,23,42,0.2)] md:max-h-[calc(100vh-3rem)]"
        onClick={(event) => event.stopPropagation()}
        data-testid="property-mapping-review-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-mapping-review-title"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#e4ebf7] bg-white px-6 py-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
              Detailed Review
            </p>
            <h3 id="property-mapping-review-title" className="mt-0.5 text-lg font-bold text-[#16213e]">
              {row.sourcePropertyName}
            </h3>
            <p className="mt-1 text-xs text-[#70809b]">
              Yardi {row.sourcePropertyCode} · {formatStatus(row.status)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#d5e0f1] bg-white"
            aria-label="Close detailed review"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading property review…
            </div>
          ) : (
            <div data-testid="property-mapping-review-body">
              <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)]">
                <PropertyDetailCard
                  title="Yardi Property Record"
                  name={row.sourcePropertyName}
                  subtitle={row.sourceAddress || 'No address'}
                  muted
                  fields={[
                    ['PMS Code', row.sourcePropertyCode],
                    ['PMC', row.pmcName || '--'],
                    ['Status', formatStatus(row.status)],
                    ['Last Checked', row.lastCheckedAt || '--'],
                    ['Match Result', row.matchReason || 'Not checked yet'],
                  ]}
                />
                <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#dce6fb] bg-gradient-to-br from-[#e6efff] to-white px-2 text-center text-[10px] font-bold text-[#3151d3]">
                  Best Liberty Match
                </div>
                {bestCandidate ? (
                  <PropertyDetailCard
                    title="Liberty Rent Property"
                    name={bestCandidate.propertyName}
                    subtitle={bestCandidate.address || 'No address'}
                    fields={[
                      ['Property ID', String(bestCandidate.propertyId)],
                      ['Address', bestCandidate.address || '--'],
                      ['Name Match', bestCandidate.flags?.nameExact ? 'Exact' : bestCandidate.flags?.nameNear ? 'Near' : 'Different'],
                      ['Score', `${bestCandidate.score}/160`],
                    ]}
                  />
                ) : (
                  <PropertyDetailCard
                    title="Liberty Rent Property"
                    name="Unmapped"
                    subtitle="No Liberty Rent property assigned"
                    fields={[
                      ['Property ID', '--'],
                      ['Address', '--'],
                      ['Name Match', 'No city/state candidate'],
                      ['Score', '0/160'],
                    ]}
                  />
                )}
              </div>

              <div className="mt-5 space-y-5">
                {row.matchReason ? (
                  <p className="text-[11px] text-[#70809b]">
                    <span className="font-semibold">Reason:</span> {row.matchReason}
                  </p>
                ) : null}

                <div>
                  <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
                    Candidate Ranking &amp; Score Breakdown
                  </h4>
                  <div className="grid gap-4 rounded-[18px] border border-[#e4ebf7] bg-white p-[18px] sm:grid-cols-2">
                    {[
                      {
                        label: 'Name',
                        value: bestCandidate?.flags?.nameExact ? 60 : bestCandidate?.flags?.nameNear ? 25 : 0,
                        max: 60,
                      },
                      {
                        label: 'Alias',
                        value: bestCandidate?.flags?.aliasExact ? 40 : bestCandidate?.flags?.aliasNear ? 15 : 0,
                        max: 40,
                      },
                      {
                        label: 'Address',
                        value: bestCandidate?.flags?.addressExact ? 35 : bestCandidate?.flags?.addressNear ? 15 : 0,
                        max: 35,
                      },
                      {
                        label: 'Overall score',
                        value: bestCandidate?.score ?? 0,
                        max: 160,
                      },
                    ].map((entry) => (
                      <div key={entry.label}>
                        <div className="mb-2 flex justify-between text-[11px] text-[#51607a]">
                          <span>{entry.label}</span>
                          <strong>
                            {entry.value}/{entry.max}
                          </strong>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-[#e8eef9]">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#27b36a] to-[#59cf86]"
                            style={{ width: `${Math.min((entry.value / entry.max) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
                    Top Ranked Liberty Properties
                  </h4>
                  {candidates.length === 0 ? (
                    <div className="rounded-[18px] border border-dashed border-[#d7e2f2] bg-white p-6 text-sm text-[#70809b]">
                      No Liberty property in this company matches the normalized city and state.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {candidates.slice(0, 5).map((candidate, index) => (
                        <div
                          key={candidate.propertyId}
                          className="rounded-[18px] border border-[#e4ebf7] bg-white p-[18px]"
                        >
                          <div className="mb-3 flex justify-between text-[11px] font-bold text-[#51607a]">
                            <span>Candidate {index + 1}</span>
                            <strong>{candidate.score}/160</strong>
                          </div>
                          <div className="rounded-xl border border-[#e4ebf7] bg-[#f8faff] p-3.5">
                            <h5 className="font-bold text-[#16213e]">{candidate.propertyName}</h5>
                            <p className="text-xs text-[#70809b]">{candidate.address || 'No address'}</p>
                            <p className="mt-2 text-xs leading-5 text-[#51607a]">
                              Property ID: {candidate.propertyId}
                              <br />
                              {(candidate.reasons || []).join(', ') ||
                                (candidate.nameMatch ? 'Exact name match' : 'No name match')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
                    Mapping History
                  </h4>
                  {history.length === 0 ? (
                    <div className="rounded-[18px] border border-[#e4ebf7] bg-white p-5 text-sm text-[#70809b]">
                      No status history has been recorded yet.
                    </div>
                  ) : (
                    <div className="space-y-3 rounded-[18px] border border-[#e4ebf7] bg-white p-5">
                      {history.map((event) => (
                        <div key={event.id} className="border-b border-[#e4ebf7] pb-3 last:border-b-0 last:pb-0">
                          <div className="text-sm font-semibold text-[#16213e]">
                            {formatStatus(event.eventType)}
                          </div>
                          {event.note ? (
                            <div className="mt-0.5 text-sm text-[#51607a]">{event.note}</div>
                          ) : null}
                          <div className="mt-1 text-xs text-[#70809b]">{event.timestamp}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  )
}

export function PropertyMappingReviewDialog({
  row,
  onClose,
}: {
  row: PropertyMappingRow | null
  onClose: () => void
}) {
  if (!row) return null
  return <PropertyMappingReviewDialogBody key={row.id} row={row} onClose={onClose} />
}
